import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
	ArrowLeft,
	Calendar,
	Clock,
	Edit,
	Pause,
	Play,
	PoundSterling,
	Share2,
	TrendingDown,
	TrendingUp,
	X,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	mapCommentFromApi,
	mapVersionFromApi,
} from "@/api/mappers/scenarioMapper";
import * as scenarioApi from "@/api/services/scenarioService";
import { getCurrentUser } from "@/api/services/userService";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { useScenarioPermission } from "@/hooks/useScenarioPermission";
import type { ScenarioMetrics } from "@/types/scenarios";
import { useApp } from "../../contexts/AppContext";
import { DEMO_USERS } from "../../contexts/UsersContext";
import { useBaseForecast } from "../../hooks/useBaseForecast";
import { useFormatting } from "../../hooks/useFormatting";
import { useScenarios } from "../../hooks/useScenarios";
import { useVisibleScenarios } from "../../hooks/useVisibleScenarios";
import { formatCoverageValue } from "../../lib/scenarioEngine";
import {
	getCashDurationShortTerm,
	getSystemTerminology,
} from "../../lib/terminology-config";
import { formatDate } from "../../lib/utils";
import type { ScenarioChange } from "../../types";
import type { ScenarioVersion } from "../../types/scenarios";
import {
	ParkScenarioModal,
	ShareScenarioModal,
} from "../modals/ScenarioModals";
import { PermissionGate } from "../permissions/PermissionGate";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { BusinessEventsModal } from "./BusinessEventsModal";
import { ScenarioBuilder } from "./builder/ScenarioBuilder";
import { ChatDiscussion } from "./ChatDiscussion";
import { DependenciesTab } from "./DependenciesTab";
import { QuickAdjustmentsModal } from "./QuickAdjustmentsModal";
import { RestoreVersionDialog } from "./RestoreVersionDialog";
import { ScenarioWorkflowActions } from "./ScenarioWorkflowActions";
import { VersionViewerModal } from "./VersionViewerModal";

interface ScenarioDetailProps {
	scenarioId: string;
	onBack: () => void;
	onEdit?: () => void;
	initialTab?: string;
}

// Helper function to get initials from name
const getInitials = (name: string): string => {
	const parts = name.split(" ");
	if (parts.length >= 2) {
		return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
	}
	return name.substring(0, 2).toUpperCase();
};

// Helper function to get consistent color for user
const getUserColor = (userId: string): string => {
	const colors = [
		"bg-blue-500",
		"bg-purple-500",
		"bg-green-500",
		"bg-orange-500",
		"bg-pink-500",
		"bg-indigo-500",
		"bg-teal-500",
		"bg-red-500",
	];

	// Simple hash function to get consistent color for user
	let hash = 0;
	for (let i = 0; i < userId.length; i++) {
		hash = userId.charCodeAt(i) + ((hash << 5) - hash);
	}
	return colors[Math.abs(hash) % colors.length];
};

export const ScenarioDetailFull: React.FC<ScenarioDetailProps> = ({
	scenarioId,
	onBack,
	onEdit,
	initialTab = "builder",
}) => {
	const { hasPermission } = usePermissions();
	const {
		addComment,
		replyToComment,
		reactToComment,
		updateScenario,
		createVersion,
		removeShare,
	} = useScenarios();
	const scenarios = useVisibleScenarios();
	const { currentUser, mode } = useApp();
	const { formatCurrency } = useFormatting();
	const { toast } = useToast();
	const { data: currentUserData } = useQuery({
		queryKey: ["currentUser"],
		queryFn: getCurrentUser,
		enabled: mode === "live",
		staleTime: 5 * 60 * 1000,
	});

	const { data: commentsData } = useQuery({
		queryKey: ["scenarioComments", Number(scenarioId)],
		queryFn: () => scenarioApi.getScenarioComments(Number(scenarioId)),
		enabled: mode === "live" && !!scenarioId,
		staleTime: 30_000,
	});
	const liveComments = useMemo(
		() => (commentsData?.comments ?? []).map(mapCommentFromApi),
		[commentsData],
	);

	const { data: versionsData } = useQuery({
		queryKey: ["scenarioVersions", Number(scenarioId)],
		queryFn: () => scenarioApi.getScenarioVersions(Number(scenarioId)),
		enabled: mode === "live" && !!scenarioId,
		staleTime: 30_000,
	});
	const liveVersions = useMemo(
		() => (versionsData?.versions ?? []).map(mapVersionFromApi),
		[versionsData],
	);
	// currentUser is already available from useApp() above
	const scenario = scenarios.find((s) => s.id === scenarioId);
	const { isOwner, canEdit, canComment } = useScenarioPermission(scenario);

	// Track whether we previously had a valid scenario (to distinguish "never loaded" from "lost access")
	const hadScenario = useRef(false);
	// Grace period: avoid flashing "Scenario not found" while context propagates
	// (e.g. after creation in live mode, the scenarios query refetch may take a few seconds)
	const [graceExpired, setGraceExpired] = useState(false);
	useEffect(() => {
		const timer = setTimeout(() => setGraceExpired(true), 3000);
		return () => clearTimeout(timer);
	}, []);
	// Cancel grace early once scenario loads
	useEffect(() => {
		if (scenario) {
			setGraceExpired(true);
		}
	}, [scenario]);
	useEffect(() => {
		if (scenario) {
			hadScenario.current = true;
		} else if (hadScenario.current) {
			// Scenario was previously visible but is no longer accessible — navigate back
			toast({
				title: "Returned to scenarios",
				description: "You no longer have access to view this scenario.",
			});
			onBack();
		}
	}, [scenario, onBack, toast]);

	// Debug: Log scenario changes
	console.log(
		"ScenarioDetailFull - Scenario:",
		scenario?.name,
		"Changes count:",
		scenario?.changes?.length,
	);

	const [shareModalOpen, setShareModalOpen] = useState(false);
	const [parkModalOpen, setParkModalOpen] = useState(false);

	const [quickAdjustmentsModalOpen, setQuickAdjustmentsModalOpen] =
		useState(false);
	const [businessEventsModalOpen, setBusinessEventsModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState(initialTab);
	const [adjustmentCount, setAdjustmentCount] = useState(0);
	const [terminology, setTerminology] = useState(getSystemTerminology());
	const [viewingVersion, setViewingVersion] = useState<ScenarioVersion | null>(
		null,
	);
	const [restoringVersion, setRestoringVersion] =
		useState<ScenarioVersion | null>(null);
	const [shareToRemove, setShareToRemove] = useState<{
		userId: string;
		displayName: string;
	} | null>(null);
	const [liveMetrics, setLiveMetrics] = useState<ScenarioMetrics | null>(null);

	// Listen for terminology updates
	useEffect(() => {
		const handleTerminologyUpdate = () => {
			setTerminology(getSystemTerminology());
		};
		window.addEventListener("terminology-updated", handleTerminologyUpdate);
		return () =>
			window.removeEventListener(
				"terminology-updated",
				handleTerminologyUpdate,
			);
	}, []);

	// Helper function to get metric name from terminology
	const getMetricName = (): string => {
		const shortTerm = getCashDurationShortTerm();
		// Capitalize first letter
		return shortTerm.charAt(0).toUpperCase() + shortTerm.slice(1);
	};

	// Helper function to get metric unit from terminology
	const getMetricUnit = (): string => {
		if (terminology.cashDuration === "date") return "";
		return "months";
	};

	// Calculate metrics from adjustments
	// Prefer live metrics from the builder when available (they reflect unsaved edits),
	// otherwise fall back to the saved scenario metrics from the context.
	const calculatedMetrics = useMemo(() => {
		const m = liveMetrics ?? scenario?.metrics;
		if (!m) {
			return {
				netImpact: 0,
				revenueChangeAmount: 0,
				costChangeAmount: 0,
				scenarioRunway: 0,
				isPositiveCashFlow: false,
				scenarioCash: 0,
				minCash: 0,
				minCashDate: new Date().toISOString(),
			};
		}
		const isPositiveCashFlow = m.runway === Infinity || m.netImpact > 50000;
		return {
			netImpact: m.netImpact,
			revenueChangeAmount: m.revenueChangeAmount,
			costChangeAmount: m.costChangeAmount,
			scenarioRunway: m.runway,
			isPositiveCashFlow,
			scenarioCash: m.endingCash,
			minCash: m.minCash,
			minCashDate:
				m.minCashDate instanceof Date
					? m.minCashDate.toISOString()
					: new Date(m.minCashDate).toISOString(),
		};
	}, [scenario?.metrics, liveMetrics]);

	// Base forecast for comparison rows in metric cards
	const {
		baseForecast: baseForecastData,
		isLoading: baseForecastLoading,
		forecastNotReady,
	} = useBaseForecast();
	const baseForecast = useMemo((): {
		currentCash: number;
		runway: number;
		minCash: number;
	} | null => {
		if (baseForecastData.length === 0) return null;
		const first = baseForecastData[0];
		let minBal = first?.balance ?? 0;
		let totalIn = 0;
		let totalOut = 0;
		for (const p of baseForecastData) {
			if (p.balance < minBal) minBal = p.balance;
			totalIn += p.incoming;
			totalOut += p.outgoing;
		}
		const avgDailyBurn = (totalOut - totalIn) / baseForecastData.length;
		const runway =
			avgDailyBurn > 0 ? (first?.balance ?? 0) / avgDailyBurn / 30.44 : 18;
		return {
			currentCash: first?.balance ?? 0,
			runway: Math.round(runway * 10) / 10,
			minCash: Math.round(minBal),
		};
	}, [baseForecastData]);

	if (!scenario) {
		if (!graceExpired && !hadScenario.current) {
			return (
				<div className="bg-background overflow-auto">
					<div className="flex items-center justify-center py-12">
						<p className="text-muted-foreground text-sm">Loading scenario…</p>
					</div>
				</div>
			);
		}
		return (
			<div className="bg-background overflow-auto">
				<div className="space-y-6">
					<div className="flex items-start gap-4">
						<Button variant="ghost" onClick={onBack}>
							<ArrowLeft className="h-4 w-4 mr-2" />
							Back
						</Button>
						<p className="text-muted-foreground mt-2">Scenario not found</p>
					</div>
				</div>
			</div>
		);
	}

	const handlePostComment = (text: string) => {
		if (text.trim()) {
			addComment(scenarioId, text);
		}
	};

	const handleRestoreVersion = (version: ScenarioVersion) => {
		if (!scenario) return;

		// Create a new version that's a copy of the selected version
		const newVersionNumber = (scenario.versions?.length || 0) + 1;
		const newVersion: ScenarioVersion = {
			id: `${scenario.id}-v${newVersionNumber}`,
			version: newVersionNumber,
			description: `Restored from v${version.version}`,
			createdBy: currentUser?.id || "unknown",
			createdByName: currentUser?.name || "Unknown User",
			createdAt: new Date(),
			data: { ...version.data },
		};

		// Update scenario with new version and restored data
		updateScenario(scenario.id, {
			...version.data,
			versions: [...(scenario.versions || []), newVersion],
		});

		// Close dialog and show success message
		setRestoringVersion(null);
		toast({
			title: "Version Restored",
			description: `Successfully restored to v${version.version}. A new version (v${newVersionNumber}) has been created.`,
		});

		// Switch to Builder tab to show restored state
		setActiveTab("builder");
	};

	const handleUpdate = (updates: Partial<typeof scenario>) => {
		if (!scenario) return;
		updateScenario(scenario.id, updates);
	};

	const handleBackWithCheck = () => {
		onBack();
	};

	return (
		<div className="bg-background">
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between gap-6">
					<div className="flex items-start gap-4 flex-1 min-w-0">
						<Button
							variant="ghost"
							onClick={handleBackWithCheck}
							className="shrink-0 mt-1"
						>
							<ArrowLeft className="h-4 w-4 mr-2" />
							Back
						</Button>
						<div className="flex-1 min-w-0">
							{/* Title row */}
							<div className="flex items-center gap-2 mb-2 flex-wrap">
								<h1 className="text-2xl font-bold text-neutral-900">
									{scenario.name}
								</h1>
								{scenario.label && (
									<Badge variant="secondary">{scenario.label}</Badge>
								)}
								<Badge
									variant={
										scenario.status === "active" ||
										scenario.status === "committed"
											? "success"
											: scenario.status === "pending-approval"
												? "warning"
												: scenario.status === "rejected"
													? "destructive"
													: "secondary"
									}
								>
									{scenario.status === "pending-approval"
										? "Pending Approval"
										: scenario.status
												.replace(/-/g, " ")
												.replace(/\b\w/g, (c) => c.toUpperCase())}
								</Badge>
								{scenario.priority !== "none" && (
									<Badge
										variant={
											scenario.priority === "critical" ||
											scenario.priority === "high"
												? "destructive"
												: scenario.priority === "medium"
													? "warning"
													: "secondary"
										}
									>
										{scenario.priority.charAt(0).toUpperCase() +
											scenario.priority.slice(1)}
									</Badge>
								)}
							</div>
							{/* Description */}
							{scenario.description && (
								<p className="text-sm text-muted-foreground mb-3">
									{scenario.description}
								</p>
							)}
							{/* Meta row */}
							<div className="flex items-center gap-4 flex-wrap">
								<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
									<Calendar className="h-3.5 w-3.5 shrink-0" />
									<span className="font-medium text-neutral-700">Created</span>
									<span>{scenario.createdByName}</span>
									<span>·</span>
									<span>{formatDate(scenario.createdAt)}</span>
								</div>
								<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
									<Clock className="h-3.5 w-3.5 shrink-0" />
									<span className="font-medium text-neutral-700">
										Last modified
									</span>
									<span>{scenario.modifiedBy || scenario.createdByName}</span>
									<span>·</span>
									<span>{formatDate(scenario.modifiedAt)}</span>
								</div>
							</div>
						</div>
					</div>
					<div className="flex flex-wrap gap-2 items-center shrink-0">
						{/* Workflow actions — permission-aware, status-driven */}
						<ScenarioWorkflowActions
							scenario={scenario}
							variant="detail"
							onStatusChanged={onBack}
						/>

						{/* Edit — non-builder tabs */}
						{onEdit && canEdit && activeTab !== "builder" && (
							<Button variant="outline" onClick={onEdit}>
								<Edit className="h-4 w-4 mr-2" />
								Edit
							</Button>
						)}

						{/* Share — owner only */}
						{isOwner && (
							<PermissionGate permission="create_scenarios">
								<Button
									variant="outline"
									onClick={() => setShareModalOpen(true)}
								>
									<Share2 className="h-4 w-4 mr-2" />
									Share
								</Button>
							</PermissionGate>
						)}

						{/* Park / Reactivate — edit or higher */}
						{canEdit && scenario.status !== "parked" && (
							<PermissionGate permission="create_scenarios">
								<Button
									variant="outline"
									onClick={() => setParkModalOpen(true)}
								>
									<Pause className="h-4 w-4 mr-2" />
									Park
								</Button>
							</PermissionGate>
						)}
						{canEdit && scenario.status === "parked" && (
							<Button variant="outline">
								<Play className="h-4 w-4 mr-2" />
								Reactivate
							</Button>
						)}
					</div>
				</div>

				{/* Permission Banner */}
				{scenario.currentUserPermission && !isOwner && (
					<div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
						<span>
							You have <strong>{scenario.currentUserPermission}</strong> access
							to this scenario.
						</span>
					</div>
				)}

				{/* Metrics Cards */}
				{(() => {
					const hasChanges = scenario.changes && scenario.changes.length > 0;
					const baseUnavailable = !baseForecast;

					if (baseForecastLoading) {
						return (
							<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
								{[
									"Cash Position",
									getMetricName(),
									"Net Impact",
									"Min Cash",
								].map((label) => (
									<Card
										key={label}
										style={{ borderLeft: "3px solid var(--mc-info-border)" }}
									>
										<CardContent className="p-4 flex flex-col justify-between h-full gap-3">
											<span className="text-base font-medium text-neutral-800">
												{label}
											</span>
											<div className="text-sm text-muted-foreground">
												Loading…
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						);
					}

					if (baseUnavailable) {
						return (
							<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
								{[
									"Cash Position",
									getMetricName(),
									"Net Impact",
									"Min Cash",
								].map((label) => (
									<Card
										key={label}
										style={{ borderLeft: "3px solid var(--mc-info-border)" }}
									>
										<CardContent className="p-4 flex flex-col justify-between h-full gap-3">
											<span className="text-base font-medium text-neutral-800">
												{label}
											</span>
											<div className="text-2xl font-bold text-muted-foreground">
												—
											</div>
											<div className="text-xs text-muted-foreground">
												{forecastNotReady
													? "Run a base forecast first"
													: "Base forecast not available"}
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						);
					}

					const cashChange = hasChanges
						? Math.round(
								(calculatedMetrics.scenarioCash - baseForecast.currentCash) /
									1000,
							) * 1000
						: 0;
					const minCashChange = hasChanges
						? Math.round(
								(calculatedMetrics.minCash - baseForecast.minCash) / 1000,
							) * 1000
						: 0;
					const runwayChange = hasChanges
						? Math.round(
								(calculatedMetrics.scenarioRunway - baseForecast.runway) * 10,
							) / 10
						: 0;
					const isPositiveCashFlow = calculatedMetrics.isPositiveCashFlow;

					return (
						<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
							{/* Cash Position */}
							<Card style={{ borderLeft: "3px solid var(--mc-info-border)" }}>
								<CardContent className="p-4 flex flex-col justify-between h-full gap-3">
									<div className="flex items-start justify-between">
										<span className="text-base font-medium text-neutral-800">
											Cash Position
										</span>
										<div
											className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
											style={{ backgroundColor: "var(--mc-info-bg)" }}
										>
											<PoundSterling
												className="h-4 w-4"
												style={{ color: "var(--mc-info-icon)" }}
											/>
										</div>
									</div>
									<div>
										<div className="text-2xl font-bold text-neutral-900 tracking-tight">
											{!hasChanges ? (
												<span className="text-muted-foreground text-base">
													—
												</span>
											) : (
												formatCurrency(calculatedMetrics.scenarioCash)
											)}
										</div>
										{hasChanges && (
											<div className="text-xs text-neutral-500 mt-0.5">
												Base: {formatCurrency(baseForecast.currentCash)}
											</div>
										)}
									</div>
									{hasChanges && (
										<div className="flex items-center gap-1 pt-2 border-t">
											{cashChange >= 0 ? (
												<TrendingUp
													className="w-4 h-4"
													style={{ color: "var(--mc-success-icon)" }}
												/>
											) : (
												<TrendingDown
													className="w-4 h-4"
													style={{ color: "var(--mc-warning-icon)" }}
												/>
											)}
											<span
												className="text-sm font-semibold"
												style={{
													color:
														cashChange >= 0
															? "var(--mc-success-icon)"
															: "var(--mc-warning-icon)",
												}}
											>
												{cashChange >= 0 ? "+" : ""}
												{formatCurrency(cashChange)}
											</span>
											{baseForecast.currentCash !== 0 && (
												<span className="text-xs text-neutral-500">
													({cashChange >= 0 ? "+" : ""}
													{(
														(cashChange / baseForecast.currentCash) *
														100
													).toFixed(1)}
													%)
												</span>
											)}
										</div>
									)}
								</CardContent>
							</Card>

							{/* Coverage / Runway */}
							<Card
								style={{ borderLeft: "3px solid var(--mc-success-border)" }}
							>
								<CardContent className="p-4 flex flex-col justify-between h-full gap-3">
									<div className="flex items-start justify-between">
										<span className="text-base font-medium text-neutral-800">
											{getMetricName()}
										</span>
										<div
											className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
											style={{ backgroundColor: "var(--mc-success-bg)" }}
										>
											<Clock
												className="h-4 w-4"
												style={{ color: "var(--mc-success-icon)" }}
											/>
										</div>
									</div>
									<div>
										{!hasChanges ? (
											<div className="text-2xl font-bold text-neutral-900 tracking-tight">
												<span className="text-muted-foreground text-base">
													—
												</span>
											</div>
										) : isPositiveCashFlow ? (
											<div
												className="text-2xl font-bold tracking-tight"
												style={{ color: "var(--mc-success-icon)" }}
											>
												Positive Cash Flow
											</div>
										) : (
											<>
												<div className="text-2xl font-bold text-neutral-900 tracking-tight">
													{(() => {
														const c = formatCoverageValue(
															calculatedMetrics.scenarioRunway,
														);
														return <span title={c.tooltip}>{c.display}</span>;
													})()}
												</div>
												{hasChanges && (
													<div className="text-xs text-neutral-500 mt-0.5">
														Base: {baseForecast.runway} months
													</div>
												)}
											</>
										)}
									</div>
									{hasChanges && !isPositiveCashFlow && (
										<div className="flex items-center gap-1 pt-2 border-t">
											{runwayChange >= 0 ? (
												<TrendingUp
													className="w-4 h-4"
													style={{ color: "var(--mc-success-icon)" }}
												/>
											) : (
												<TrendingDown
													className="w-4 h-4"
													style={{ color: "var(--mc-warning-icon)" }}
												/>
											)}
											<span
												className="text-sm font-semibold"
												style={{
													color:
														runwayChange >= 0
															? "var(--mc-success-icon)"
															: "var(--mc-warning-icon)",
												}}
											>
												{runwayChange >= 0 ? "+" : ""}
												{runwayChange} months
											</span>
										</div>
									)}
								</CardContent>
							</Card>

							{/* Net Impact */}
							<Card style={{ borderLeft: "3px solid var(--mc-info-border)" }}>
								<CardContent className="p-4 flex flex-col justify-between h-full gap-3">
									<div className="flex items-start justify-between">
										<span className="text-base font-medium text-neutral-800">
											Net Impact
										</span>
										<div
											className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
											style={{ backgroundColor: "var(--mc-info-bg)" }}
										>
											{hasChanges && calculatedMetrics.netImpact >= 0 ? (
												<TrendingUp
													className="h-4 w-4"
													style={{ color: "var(--mc-info-icon)" }}
												/>
											) : (
												<TrendingDown
													className="h-4 w-4"
													style={{ color: "var(--mc-info-icon)" }}
												/>
											)}
										</div>
									</div>
									<div>
										<div className="text-2xl font-bold text-neutral-900 tracking-tight">
											{!hasChanges ? (
												<span className="text-muted-foreground text-base">
													—
												</span>
											) : (
												<span>
													{calculatedMetrics.netImpact >= 0 ? "+" : ""}
													{formatCurrency(calculatedMetrics.netImpact)}
												</span>
											)}
										</div>
										{hasChanges && (
											<div className="text-xs text-neutral-500 mt-0.5">
												vs base forecast
											</div>
										)}
									</div>
								</CardContent>
							</Card>

							{/* Min Cash */}
							<Card style={{ borderLeft: "3px solid var(--mc-info-border)" }}>
								<CardContent className="p-4 flex flex-col justify-between h-full gap-3">
									<div className="flex items-start justify-between">
										<span className="text-base font-medium text-neutral-800">
											Min Cash
										</span>
										<div
											className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
											style={{ backgroundColor: "var(--mc-info-bg)" }}
										>
											<PoundSterling
												className="h-4 w-4"
												style={{ color: "var(--mc-info-icon)" }}
											/>
										</div>
									</div>
									<div>
										<div className="text-2xl font-bold text-neutral-900 tracking-tight">
											{!hasChanges ? (
												<span className="text-muted-foreground text-base">
													No changes yet
												</span>
											) : (
												formatCurrency(calculatedMetrics.minCash)
											)}
										</div>
										{hasChanges && (
											<div className="text-xs text-neutral-500 mt-0.5">
												Base: {formatCurrency(baseForecast.minCash)} ·{" "}
												{formatDate(new Date(calculatedMetrics.minCashDate))}
											</div>
										)}
									</div>
									{hasChanges && (
										<div className="flex items-center gap-1 pt-2 border-t">
											{minCashChange >= 0 ? (
												<TrendingUp
													className="w-4 h-4"
													style={{ color: "var(--mc-success-icon)" }}
												/>
											) : (
												<TrendingDown
													className="w-4 h-4"
													style={{ color: "var(--mc-warning-icon)" }}
												/>
											)}
											<span
												className="text-sm font-semibold"
												style={{
													color:
														minCashChange >= 0
															? "var(--mc-success-icon)"
															: "var(--mc-warning-icon)",
												}}
											>
												{minCashChange >= 0 ? "+" : ""}
												{formatCurrency(minCashChange)}
											</span>
										</div>
									)}
								</CardContent>
							</Card>
						</div>
					);
				})()}

				{/* Tabs */}
				<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
					<TabsList>
						<TabsTrigger value="builder">
							Builder{adjustmentCount > 0 ? ` (${adjustmentCount})` : ""}
						</TabsTrigger>
						<TabsTrigger value="impact">Impact</TabsTrigger>
						<TabsTrigger value="discussion">
							{(() => {
								const count =
									mode === "live"
										? liveComments.length
										: scenario.comments?.length || 0;
								return count > 0 ? `Discussion (${count})` : "Discussion";
							})()}
						</TabsTrigger>
						<TabsTrigger value="history">
							{(() => {
								const versions =
									mode === "live" ? liveVersions : scenario.versions || [];
								return versions.length > 0
									? `History (v${Math.max(...versions.map((v) => v.version))})`
									: "History";
							})()}
						</TabsTrigger>
						<TabsTrigger value="dependencies">
							{(() => {
								if (!scenario.dependencies) return "Dependencies";
								if (Array.isArray(scenario.dependencies)) {
									return scenario.dependencies.length > 0
										? `Dependencies (${scenario.dependencies.length})`
										: "Dependencies";
								}
								const count =
									(scenario.dependencies.prerequisites?.length || 0) +
									(scenario.dependencies.conflicts?.length || 0);
								return count > 0 ? `Dependencies (${count})` : "Dependencies";
							})()}
						</TabsTrigger>
						<TabsTrigger value="sharing">
							{(() => {
								if (!scenario.shares || scenario.shares.length === 0)
									return "Sharing";
								const count = scenario.shares.filter(
									(share: { userId?: string }) => share && share.userId,
								).length;
								return count > 0 ? `Sharing (${count})` : "Sharing";
							})()}
						</TabsTrigger>
					</TabsList>

					<TabsContent value="builder" className="space-y-4">
						<ScenarioBuilder
							scenarioId={scenarioId}
							onAdjustmentCountChange={setAdjustmentCount}
							onLiveMetricsChange={setLiveMetrics}
							readOnly={!canEdit}
						/>
					</TabsContent>

					<TabsContent value="impact" className="space-y-4">
						<Card>
							<CardHeader>
								<CardTitle>Scenario Impact Analysis</CardTitle>
							</CardHeader>
							<CardContent className="space-y-6">
								{/* Impact Breakdown */}
								<div>
									<h3 className="text-lg font-semibold mb-4">
										Impact Breakdown
									</h3>
									<div className="space-y-3">
										<div className="flex items-center justify-between p-3 border rounded-lg">
											<div>
												<div className="font-medium">Revenue Changes</div>
												<div className="text-sm text-muted-foreground">
													{
														(scenario.changes || []).filter(
															(c) =>
																c.type?.toLowerCase().includes("revenue") ||
																c.name?.toLowerCase().includes("revenue"),
														).length
													}{" "}
													adjustment
													{(scenario.changes || []).filter(
														(c) =>
															c.type?.toLowerCase().includes("revenue") ||
															c.name?.toLowerCase().includes("revenue"),
													).length !== 1
														? "s"
														: ""}
												</div>
											</div>
											<div
												className={`text-lg font-semibold ${calculatedMetrics.revenueChangeAmount >= 0 ? "text-green-600" : "text-red-600"}`}
											>
												{calculatedMetrics.revenueChangeAmount >= 0 ? "+" : ""}
												{formatCurrency(calculatedMetrics.revenueChangeAmount)}
											</div>
										</div>
										<div className="flex items-center justify-between p-3 border rounded-lg">
											<div>
												<div className="font-medium">Cost Changes</div>
												<div className="text-sm text-muted-foreground">
													{
														(scenario.changes || []).filter(
															(c) =>
																c.type?.toLowerCase().includes("cost") ||
																c.name?.toLowerCase().includes("cost") ||
																c.name?.toLowerCase().includes("marketing") ||
																c.name?.toLowerCase().includes("expense"),
														).length
													}{" "}
													adjustment
													{(scenario.changes || []).filter(
														(c) =>
															c.type?.toLowerCase().includes("cost") ||
															c.name?.toLowerCase().includes("cost") ||
															c.name?.toLowerCase().includes("marketing") ||
															c.name?.toLowerCase().includes("expense"),
													).length !== 1
														? "s"
														: ""}
												</div>
											</div>
											<div
												className={`text-lg font-semibold ${calculatedMetrics.costChangeAmount <= 0 ? "text-green-600" : "text-red-600"}`}
											>
												{calculatedMetrics.costChangeAmount >= 0 ? "+" : ""}
												{formatCurrency(calculatedMetrics.costChangeAmount)}
											</div>
										</div>
										<div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
											<div>
												<div className="font-medium">Net Impact</div>
												<div className="text-sm text-muted-foreground">
													Total scenario effect
												</div>
											</div>
											<div
												className={`text-xl font-bold ${calculatedMetrics.netImpact >= 0 ? "text-green-600" : "text-red-600"}`}
											>
												{calculatedMetrics.netImpact >= 0 ? "+" : ""}
												{formatCurrency(calculatedMetrics.netImpact)}
											</div>
										</div>
									</div>
								</div>

								{/* Risk Assessment */}
								<div>
									<h3 className="text-lg font-semibold mb-4">
										Risk Assessment
									</h3>
									<div className="border rounded-lg p-4">
										<div className="flex items-center gap-3 mb-3">
											<div
												className={`w-3 h-3 rounded-full ${
													calculatedMetrics.minCash > 100000
														? "bg-green-500"
														: calculatedMetrics.minCash > 50000
															? "bg-amber-500"
															: "bg-red-500"
												}`}
											/>
											<div className="font-medium">
												{calculatedMetrics.minCash > 100000
													? "Low Risk"
													: calculatedMetrics.minCash > 50000
														? "Medium Risk"
														: "High Risk"}
											</div>
										</div>
										<p className="text-sm text-muted-foreground">
											{calculatedMetrics.minCash > 100000
												? "Minimum cash position remains healthy throughout the scenario period."
												: calculatedMetrics.minCash > 50000
													? "Minimum cash position requires monitoring. Consider contingency planning."
													: "Minimum cash position is critical. Immediate action may be required."}
										</p>
									</div>
								</div>

								{/* Adjustments Summary */}
								{scenario.changes && scenario.changes.length > 0 && (
									<div>
										<h3 className="text-lg font-semibold mb-4">
											Adjustments Applied ({scenario.changes.length})
										</h3>
										<div className="space-y-2">
											{scenario.changes.map((change) => (
												<div
													key={change.id}
													className="flex items-center justify-between p-3 border rounded-lg"
												>
													<div className="flex-1">
														<div className="font-medium">{change.name}</div>
														<div className="text-sm text-muted-foreground">
															{change.description}
														</div>
													</div>
													<div
														className={`font-semibold ${change.amount >= 0 ? "text-green-600" : "text-red-600"}`}
													>
														{change.amount >= 0 ? "+" : ""}
														{formatCurrency(change.amount)}
													</div>
												</div>
											))}
										</div>
									</div>
								)}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="discussion" className="space-y-4">
						<ChatDiscussion
							comments={
								mode === "live" ? liveComments : scenario.comments || []
							}
							currentUserId={
								mode === "live" && currentUserData?.id
									? currentUserData.id
									: currentUser?.id || "unknown"
							}
							currentUserName={
								mode === "live" && currentUserData?.firstName
									? `${currentUserData.firstName} ${currentUserData.lastName ?? ""}`.trim()
									: currentUser?.name || "Unknown User"
							}
							onPostComment={handlePostComment}
							readOnly={!canComment}
						/>

						{/* Approval History — live mode only */}
						{mode === "live" &&
							scenario.approvalHistory &&
							scenario.approvalHistory.length > 0 && (
								<Card>
									<CardHeader>
										<CardTitle className="text-base">
											Approval History
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-2">
										{scenario.approvalHistory.map((approval) => (
											<div
												key={approval.id}
												className="flex items-start gap-2 text-sm"
											>
												<Badge
													variant="outline"
													className={
														approval.status === "approved"
															? "bg-green-50 text-green-700 border-green-200"
															: approval.status === "rejected"
																? "bg-red-50 text-red-700 border-red-200"
																: "bg-amber-50 text-amber-700 border-amber-200"
													}
												>
													{approval.status}
												</Badge>
												<div>
													<span className="font-medium">
														{approval.userName}
													</span>
													<span className="text-muted-foreground">
														{" "}
														&mdash; {approval.userRole}
													</span>
													{approval.comments && (
														<p className="text-muted-foreground mt-0.5">
															{approval.comments}
														</p>
													)}
													<p className="text-xs text-muted-foreground">
														{formatDate(approval.timestamp)}
													</p>
												</div>
											</div>
										))}
									</CardContent>
								</Card>
							)}
					</TabsContent>

					<TabsContent value="dependencies" className="space-y-4">
						<DependenciesTab
							scenario={scenario}
							onUpdate={handleUpdate}
							readOnly={!canEdit}
						/>
					</TabsContent>

					<TabsContent value="history" className="space-y-4">
						<Card>
							<CardHeader>
								<CardTitle>Version History</CardTitle>
							</CardHeader>
							<CardContent>
								{(() => {
									const displayVersions =
										mode === "live" ? liveVersions : scenario.versions || [];
									return displayVersions.length > 0 ? (
										<div className="space-y-3">
											{/* Sort versions in reverse chronological order (highest version number first) */}
											{[...displayVersions]
												.sort((a, b) => b.version - a.version)
												.map((version, index) => {
													// The highest version number is the current version
													const isCurrentVersion = index === 0;

													return (
														<div
															key={version.version}
															className="border rounded-lg p-4"
														>
															<div className="flex items-start justify-between">
																<div>
																	<div className="font-medium">
																		v{version.version}
																		{isCurrentVersion && (
																			<Badge className="ml-2" variant="outline">
																				Current
																			</Badge>
																		)}
																	</div>
																	<div className="text-sm text-muted-foreground mt-1">
																		{version.description}
																	</div>
																	<div className="text-xs text-muted-foreground mt-1">
																		{version.createdByName} •{" "}
																		{formatDate(version.createdAt)}
																	</div>
																</div>
																<div className="flex gap-2">
																	<Button
																		variant="ghost"
																		size="sm"
																		onClick={() => setViewingVersion(version)}
																	>
																		View
																	</Button>
																	{!isCurrentVersion && canEdit && (
																		<Button
																			variant="ghost"
																			size="sm"
																			onClick={() =>
																				setRestoringVersion(version)
																			}
																		>
																			Restore
																		</Button>
																	)}
																</div>
															</div>
														</div>
													);
												})}
										</div>
									) : (
										<div className="text-center py-8 text-muted-foreground">
											No version history available
										</div>
									);
								})()}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="sharing" className="space-y-4">
						<Card>
							<CardHeader>
								<CardTitle>Shared With</CardTitle>
							</CardHeader>
							<CardContent>
								{scenario.shares && scenario.shares.length > 0 ? (
									<div className="space-y-3">
										{scenario.shares
											.filter((share: any) => share && share.userId)
											.map((share: any) => {
												const demoUser = share.userId
													? DEMO_USERS.find((u) => u.id === share.userId)
													: null;
												const displayName =
													mode === "live"
														? share.userName || "Unknown User"
														: demoUser?.name || "Unknown User";
												const displayRole =
													mode === "live"
														? ""
														: demoUser?.forecasterRole || "Unknown Role";
												return (
													<div
														key={share.id || Math.random()}
														className="flex items-center justify-between p-3 border rounded-lg"
													>
														<div className="flex items-center gap-3">
															<div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
																{displayName !== "Unknown User"
																	? displayName
																			.split(" ")
																			.map((n: string) => n[0])
																			.join("")
																	: "?"}
															</div>
															<div>
																<div className="font-medium">{displayName}</div>
																{displayRole && (
																	<div className="text-sm text-muted-foreground">
																		{displayRole}
																	</div>
																)}
																<div className="text-sm text-muted-foreground mt-1">
																	{share.permission === "edit"
																		? "Can edit"
																		: share.permission === "comment"
																			? "Can comment"
																			: "Can view"}{" "}
																	• Shared{" "}
																	{share.sharedAt
																		? formatDate(
																				typeof share.sharedAt === "string"
																					? new Date(share.sharedAt)
																					: share.sharedAt,
																			)
																		: "recently"}
																</div>
															</div>
														</div>
														{isOwner && (
															<Button
																variant="ghost"
																size="sm"
																onClick={() => {
																	setShareToRemove({
																		userId: share.userId,
																		displayName: displayName,
																	});
																}}
															>
																<X className="h-4 w-4" />
															</Button>
														)}
													</div>
												);
											})}
									</div>
								) : (
									<p className="text-sm text-muted-foreground mb-4">
										Not shared with anyone yet
									</p>
								)}

								{isOwner && hasPermission("create_scenarios") && (
									<Button
										onClick={() => setShareModalOpen(true)}
										className="mt-4 w-full"
										variant="outline"
									>
										<Share2 className="mr-2 h-4 w-4" />
										Share Scenario
									</Button>
								)}
							</CardContent>
						</Card>
					</TabsContent>
				</Tabs>
			</div>

			{/* Modals */}
			{shareModalOpen && (
				<ShareScenarioModal
					scenario={scenario}
					onClose={() => setShareModalOpen(false)}
				/>
			)}
			{parkModalOpen && (
				<ParkScenarioModal
					scenario={scenario}
					onClose={() => setParkModalOpen(false)}
				/>
			)}

			<QuickAdjustmentsModal
				open={quickAdjustmentsModalOpen}
				onOpenChange={setQuickAdjustmentsModalOpen}
				onSelectAdjustment={(adjustmentType, adjustmentData) => {
					if (adjustmentType === "delay_invoice" && adjustmentData) {
						// Add the adjustment to the scenario
						const newChange: ScenarioChange = {
							id: `change-${Date.now()}`,
							type: "delay-payment",
							name: `Delay Invoice #${adjustmentData.invoiceNumber}`,
							description: `${adjustmentData.customer} - Delay payment from ${format(
								adjustmentData.originalDueDate,
								"MMM d",
							)} to ${format(adjustmentData.newDueDate, "MMM d")}${
								adjustmentData.reason ? ` - ${adjustmentData.reason}` : ""
							}`,
							amount: adjustmentData.amount,
							date: adjustmentData.newDueDate,
							details: {
								customer: adjustmentData.customer,
								invoiceNumber: adjustmentData.invoiceNumber,
							},
							createdBy: "current-user",
							createdAt: new Date(),
						};

						updateScenario(scenario.id, {
							changes: [...scenario.changes, newChange],
						});
					}
				}}
			/>

			<BusinessEventsModal
				open={businessEventsModalOpen}
				onOpenChange={setBusinessEventsModalOpen}
				onSelectEvent={(eventType, eventData) => {
					console.log("Selected event:", eventType, eventData);
				}}
			/>

			{/* Version Viewer Modal */}
			{viewingVersion && (
				<VersionViewerModal
					version={viewingVersion}
					open={!!viewingVersion}
					onClose={() => setViewingVersion(null)}
				/>
			)}

			{/* Remove Share Confirmation Dialog */}
			<AlertDialog
				open={!!shareToRemove}
				onOpenChange={() => setShareToRemove(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove access?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to remove{" "}
							<strong>{shareToRemove?.displayName}</strong> from this scenario?
							They will no longer be able to access it.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setShareToRemove(null)}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (shareToRemove) {
									removeShare(scenario.id, shareToRemove.userId);
									setShareToRemove(null);
								}
							}}
						>
							Remove
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Restore Version Dialog */}
			{restoringVersion && (
				<RestoreVersionDialog
					open={!!restoringVersion}
					onClose={() => setRestoringVersion(null)}
					onConfirm={() => handleRestoreVersion(restoringVersion)}
					versionNumber={restoringVersion.version}
					currentVersion={(() => {
						const versions =
							mode === "live" ? liveVersions : scenario.versions || [];
						return versions.length > 0
							? Math.max(...versions.map((v) => v.version))
							: 1;
					})()}
				/>
			)}
		</div>
	);
};
